/**
 * ConsentService.ts
 * Thread: T-051 — Cookie & LocalStorage Consent Banner
 *
 * Stateless consent management service for GDPR / Israeli Privacy Protection compliance.
 * Manages three consent categories with strict opt-in defaults (non-essential = false).
 */

const CONSENT_KEY = 'spirit_consent_preferences';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ConsentPreferences {
  /** Always true — required for Supabase Auth and consent storage itself. */
  essential: true;
  /** Controls caching of user UX state: recent cities, calculator inputs, saved variations. */
  functional: boolean;
  /** Controls AnalyticsService writes: last-active tracking, page-view telemetry, error logs. */
  analytics: boolean;
  /** ISO timestamp of when the user last made a consent choice. */
  timestamp: string;
}

/** Safe subset accepted by setPreferences — essential cannot be overridden. */
export type ConsentUpdate = Partial<Omit<ConsentPreferences, 'essential' | 'timestamp'>>;

// ---------------------------------------------------------------------------
// Defaults (strict opt-in — all non-essential default false)
// ---------------------------------------------------------------------------

const DEFAULT_PREFERENCES: ConsentPreferences = {
  essential: true,
  functional: false,
  analytics: false,
  timestamp: '',
};

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function readRaw(): ConsentPreferences | null {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<ConsentPreferences>;
    // Guarantee essential is always true regardless of stored value
    return {
      ...DEFAULT_PREFERENCES,
      ...parsed,
      essential: true,
    };
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Returns the stored consent preferences, or strict opt-in defaults if no
 * choice has been recorded yet.
 */
export function getPreferences(): ConsentPreferences {
  return readRaw() ?? { ...DEFAULT_PREFERENCES };
}

/**
 * Saves a partial consent update. Always locks `essential: true` and stamps
 * the current ISO timestamp.
 */
export function setPreferences(update: ConsentUpdate): void {
  try {
    const current = readRaw() ?? { ...DEFAULT_PREFERENCES };
    const next: ConsentPreferences = {
      ...current,
      ...update,
      essential: true,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem(CONSENT_KEY, JSON.stringify(next));
  } catch {
    // localStorage unavailable (e.g. private browsing quota exceeded) — fail silently
  }
}

/**
 * Returns true if the user has previously made any consent choice (banner
 * should be hidden).
 */
export function hasSelected(): boolean {
  try {
    const raw = readRaw();
    return raw !== null && raw.timestamp !== '';
  } catch {
    return false;
  }
}

/**
 * Clears all stored consent preferences. Intended for dev/debug use only.
 * Calling this will cause the banner to re-appear on next render.
 */
export function clearPreferences(): void {
  try {
    localStorage.removeItem(CONSENT_KEY);
  } catch {
    // no-op
  }
}

// ---------------------------------------------------------------------------
// Re-prompt Logic (24-hour window for partial consent)
// ---------------------------------------------------------------------------

/** Re-prompt window: 24 hours in milliseconds. */
const REPROMPT_INTERVAL_MS = 24 * 60 * 60 * 1000;

/**
 * Returns true only if the user has accepted ALL non-essential categories
 * (functional AND analytics). Used to skip the 24h re-prompt entirely.
 */
export function isFullConsent(): boolean {
  const prefs = readRaw();
  return prefs !== null && prefs.functional === true && prefs.analytics === true;
}

/**
 * Determines whether the consent banner should be shown on mount.
 *
 * Rules:
 * - No consent recorded yet            → true  (show immediately)
 * - Full consent (functional+analytics) → false (never re-prompt)
 * - Partial consent + < 24h elapsed    → false (respect the choice)
 * - Partial consent + ≥ 24h elapsed    → true  (re-prompt politely)
 */
export function shouldShowBanner(): boolean {
  const prefs = readRaw();

  // No record at all — first visit
  if (!prefs || prefs.timestamp === '') return true;

  // Full consent — never bother again
  if (prefs.functional && prefs.analytics) return false;

  // Partial consent — re-prompt after 24h
  const lastTs = new Date(prefs.timestamp).getTime();
  return Date.now() - lastTs >= REPROMPT_INTERVAL_MS;
}

